/**
 * Outer fade for open marketing assets
 *
 * Soft-dissolves an asset into the surface behind it (page or dark plate).
 * Never paints a light inset frame — edges match the parent surface, center stays clear.
 */

import { cn } from '@/lib/utils'

export function MarketingAssetOuterFade({
  tone = 'page',
  className
}: {
  tone?: 'page' | 'dark-plate'
  className?: string
}) {
  // Page cream / near-black page — only when the asset sits on the open page.
  const pageEdge =
    'from-[rgb(241,245,239)] dark:from-[rgb(4,8,14)] via-[rgb(241,245,239)]/70 dark:via-[rgb(4,8,14)]/70'
  // Plate is always navy — even in light mode. Cream here is what made the "light postcard frame".
  const plateEdge =
    'from-slate-950 via-slate-950/75 dark:from-[rgb(10,14,20)] dark:via-[rgb(10,14,20)]/75'

  const edge = tone === 'dark-plate' ? plateEdge : pageEdge
  const radial =
    tone === 'dark-plate'
      ? 'bg-[radial-gradient(ellipse_72%_68%_at_50%_50%,transparent_48%,rgba(2,6,23,0.55)_78%,rgb(2,6,23)_100%)] dark:bg-[radial-gradient(ellipse_72%_68%_at_50%_50%,transparent_48%,rgba(10,14,20,0.55)_78%,rgb(10,14,20)_100%)]'
      : 'bg-[radial-gradient(ellipse_72%_68%_at_50%_50%,transparent_48%,rgba(241,245,239,0.55)_78%,rgb(241,245,239)_100%)] dark:bg-[radial-gradient(ellipse_72%_68%_at_50%_50%,transparent_48%,rgba(4,8,14,0.55)_78%,rgb(4,8,14)_100%)]'

  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 rounded-[inherit]',
        className
      )}
    >
      <div className={cn('absolute inset-0', radial)} />
      <div
        className={cn(
          'absolute inset-x-0 top-0 h-[18%] bg-gradient-to-b to-transparent',
          edge
        )}
      />
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 h-[22%] bg-gradient-to-t to-transparent',
          edge
        )}
      />
      <div
        className={cn(
          'absolute inset-y-0 left-0 w-[16%] bg-gradient-to-r to-transparent',
          edge
        )}
      />
      <div
        className={cn(
          'absolute inset-y-0 right-0 w-[18%] bg-gradient-to-l to-transparent',
          edge
        )}
      />
    </div>
  )
}

/** Soft-edge mask for open asset frames — dissolves corners outward, no hard crop. */
export const marketingAssetSoftEdgeClassName =
  'overflow-hidden rounded-[1.75rem] [mask-image:radial-gradient(ellipse_92%_88%_at_50%_50%,#000_52%,transparent_100%)] [-webkit-mask-image:radial-gradient(ellipse_92%_88%_at_50%_50%,#000_52%,transparent_100%)]'
